import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowRight, ScanLine, ChefHat, Sparkles, Store, CheckCircle2, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Logo, ReceiptSketch } from "../components/Decor";

const STEPS = [
  { id: 1, icon: Store, title: "Votre établissement", desc: "Confirmez le nom et vos préférences." },
  { id: 2, icon: ScanLine, title: "Votre première facture", desc: "Scannez ou saisissez pour lancer les calculs." },
  { id: 3, icon: ChefHat, title: "Votre premier plat", desc: "Créez la fiche du plat que vous vendez le plus." },
  { id: 4, icon: Sparkles, title: "Votre tableau de bord", desc: "Découvrez votre nouvel espace." },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState(user?.establishment_name || "");
  const [demoLoading, setDemoLoading] = useState(false);
  const next = () => (step < 4 ? setStep(step + 1) : nav("/aujourdhui"));
  const skip = () => nav("/aujourdhui");

  const loadDemo = async () => {
    setDemoLoading(true);
    try {
      // Feb 2026 — serveur idempotent : no-op si la démo est déjà en base.
      const res = await api.loadDemo();
      if (res && res.loaded === false) {
        toast.info("Démo déjà présente — aucune donnée réinjectée.");
      } else {
        toast.success("Démo chargée — 3 mois de factures fournisseurs prêtes à explorer.");
      }
      setStep(4);
    } catch { toast.error("Erreur lors du chargement de la démo."); }
    finally { setDemoLoading(false); }
  };

  const saveEstablishment = async () => {
    if (!name.trim()) return next();
    try { await api.updateSettings({ establishment_name: name.trim() }); } catch { /* soft */ }
    next();
  };

  const StepIcon = STEPS[step - 1].icon;

  return (
    <div className="min-h-screen bg-[#F4F5EF]" data-testid="onboarding-page">
      {/* Deep blue top strip */}
      <div className="relative bg-[#0B1F33] text-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 pat-grid-ink opacity-40" />
        <div aria-hidden className="absolute -right-10 top-2 opacity-70 hidden md:block"><ReceiptSketch w={170} tone="dark" /></div>
        <div className="relative max-w-4xl mx-auto w-full px-5 py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={30} />
            <span className="font-heading text-[15.5px] font-semibold tracking-tight">RentaScan</span>
          </div>
          <button onClick={skip} data-testid="onboarding-skip" className="text-[12.5px] text-white/70 hover:text-white">
            Ignorer et accéder à l'application →
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 -mt-8 pb-16">
        <div className="rounded-xl bg-white border border-[#CDD7E3] shadow-[0_8px_28px_rgba(18,53,91,0.08)] p-6 sm:p-8">
          {/* Progress */}
          <div className="flex items-center gap-3 mb-8" data-testid="onboarding-progress">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex-1 flex flex-col items-center relative">
                {i > 0 && <div className={`absolute left-[-50%] top-4 w-full h-[2px] ${s.id <= step ? "bg-[#FF9F1C]" : "bg-[#CDD7E3]"}`} />}
                <div
                  className={`relative w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold ${
                    s.id < step ? "bg-[#24845B] text-white" :
                    s.id === step ? "bg-[#FF9F1C] text-[#111111] ring-4 ring-[#FFF0D2]" :
                    "bg-white border border-[#CDD7E3] text-[#5E6875]"
                  }`}
                  data-testid={`step-${s.id}-indicator`}
                >
                  {s.id < step ? <CheckCircle2 className="w-4 h-4" /> : s.id}
                </div>
                <div className={`text-[10.5px] mt-2 text-center leading-tight ${s.id === step ? "text-[#111111] font-semibold" : "text-[#5E6875]"}`}>
                  {s.title}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-[#0B1F33] text-[#FF9F1C] flex items-center justify-center shrink-0">
              <StepIcon className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#1769E0] font-semibold">Étape {step} / 4</div>
              <h1 className="text-[22px] font-heading font-bold text-[#111111]">{STEPS[step - 1].title}</h1>
              <p className="mt-1 text-[13px] text-[#5E6875]">{STEPS[step - 1].desc}</p>
            </div>
          </div>

          <div className="mt-6">
            {step === 1 && (
              <div className="space-y-4" data-testid="step-1-content">
                <div>
                  <label className="block text-[12px] font-semibold mb-1.5 text-[#111111]">Nom de votre établissement</label>
                  <input value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="onboarding-establishment"
                    className="w-full h-11 px-3 rounded-md border border-[#CDD7E3] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2878FF]/25 focus:border-[#2878FF]" />
                  <p className="mt-1 text-[11px] text-[#5E6875]">Utilisé dans le menu QR et vos exports PDF.</p>
                </div>
                <button onClick={saveEstablishment} data-testid="onboarding-next-1" className="h-11 px-5 rounded-md text-[#111111] text-[13.5px] font-semibold inline-flex items-center gap-2 bg-[#FF9F1C] hover:bg-[#F58F06]">
                  Continuer <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-2-content">
                <p className="text-[13.5px] text-[#111111]">Deux façons de commencer :</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button onClick={() => nav("/factures")} data-testid="onboarding-scan" className="text-left rounded-lg border-2 border-[#CDD7E3] bg-white p-4 hover:border-[#1769E0] hover:bg-[#DCEBFF]/30 transition-all">
                    <ScanLine className="w-5 h-5 mb-2 text-[#1769E0]" />
                    <div className="text-[14px] font-semibold">Scanner une facture</div>
                    <div className="text-[12px] text-[#5E6875] mt-1">Photographiez ou importez un fichier — RentaScan reconnaît tout.</div>
                  </button>
                  <button onClick={loadDemo} disabled={demoLoading} data-testid="onboarding-demo" className="text-left rounded-lg border-2 border-[#FFD166] bg-[#FFF0D2]/50 p-4 hover:border-[#FF9F1C] transition-all disabled:opacity-70">
                    <Sparkles className="w-5 h-5 mb-2 text-[#F58F06]" />
                    <div className="text-[14px] font-semibold inline-flex items-center gap-2">
                      Utiliser des données de démo {demoLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    </div>
                    <div className="text-[12px] text-[#5E6875] mt-1">3 mois de factures fournisseurs, hausse coppa +16 %.</div>
                  </button>
                </div>
                <div className="flex justify-between pt-2">
                  <button onClick={() => setStep(1)} className="text-[13px] text-[#5E6875] hover:text-[#111111]">← Retour</button>
                  <button onClick={next} data-testid="onboarding-next-2" className="text-[13px] font-semibold text-[#1769E0] hover:text-[#0B1F33]">Continuer sans facture →</button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-3-content">
                <p className="text-[13.5px] text-[#111111]">Créez la fiche du plat que vous vendez le plus. Vous pourrez suivre sa rentabilité et l'ajuster dès qu'un prix d'achat change.</p>
                <div className="flex gap-3">
                  <button onClick={() => nav("/plats")} data-testid="onboarding-create-recipe" className="h-11 px-5 rounded-md text-[#111111] text-[13.5px] font-semibold inline-flex items-center gap-2 bg-[#FF9F1C] hover:bg-[#F58F06]">
                    <ChefHat className="w-4 h-4" /> Créer un plat
                  </button>
                  <button onClick={next} data-testid="onboarding-next-3" className="h-11 px-5 rounded-md border border-[#CDD7E3] bg-white text-[13.5px] text-[#111111] font-medium">
                    Plus tard
                  </button>
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-4" data-testid="step-4-content">
                <div className="rounded-lg bg-[#E4F4EA] border border-[#C0E1CC] p-4">
                  <CheckCircle2 className="w-5 h-5 mb-2 text-[#24845B]" />
                  <div className="text-[14.5px] font-semibold text-[#1B5C39]">Tout est prêt.</div>
                  <div className="text-[12.5px] text-[#1B5C39]/80 mt-1">Votre tableau de bord vous montrera les hausses détectées, les plats à optimiser et le gain potentiel dès la première facture analysée.</div>
                </div>
                <button onClick={() => nav("/aujourdhui")} data-testid="onboarding-finish" className="h-11 px-5 rounded-md text-[#111111] text-[14px] font-semibold inline-flex items-center gap-2 bg-[#FF9F1C] hover:bg-[#F58F06]">
                  Accéder à mon espace <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
